import { useState } from "react"
import { Tooltip } from "antd"
import { WarningOrangeIcon } from "components/ui/icons/Warning/WarningOrangeIcon"
import {
	defaultTooltipStyles,
	getShadowTooltipContainer,
} from "components/ui/customWebviewer/shared/shadowTooltip"
import { SelectModelNumberModal, type TModelNumberTarget } from "./SelectModelNumberModal/SelectModelNumberModal"
import type { TDimensionGroup, TSelectedModelNumber } from "types/elevationReferences.type"
import type { TCustomModelNumber, TCustomModelNumberSummary } from "types/customModelNumbers.type"
import type { TSelectableModelNumber } from "types/modelNumbers.type"
import style from "./ModelNumberSection.module.scss"

const { section, headerRow, titleGroup, title, requiredMark, editLink, tag, tagRow, warningIcon, selectLink } = style

type Props = {
	furniture: TModelNumberTarget
	selectedModelNumber: TSelectedModelNumber | null
	seriesOverrideId: number | null
	missingPriceSeriesName: string | null
	/**
	 * `useArbitraryEndpoint` is set when the pick came from the modal's
	 * catalogue-wide scope and may therefore belong to another category — see the
	 * modal's `onSave` for why that needs a different endpoint. `customModelNumber`
	 * (#1346) is set when the pick came from the Custom tab, and supersedes both of
	 * the others: that save goes through the custom-model-number endpoint.
	 */
	onSelect: (
		model: TSelectedModelNumber,
		options: {
			customModelNumber: TCustomModelNumber | null
			useArbitraryEndpoint: boolean
			categoryNodeId: number | null
		},
	) => Promise<void>
	isOther?: boolean
	isRequired?: boolean
	/**
	 * The Custom Model Number this object is linked to (#1346), when it is a custom object. It is
	 * what the chip reads — a custom object has no `selected_model_number` — and it opens the picker
	 * on the Custom tab with that row already selected.
	 */
	customModelNumber?: TCustomModelNumberSummary | null
	/** The object IS a custom object, even when nothing is linked to it yet. */
	isCustom?: boolean
	/**
	 * REQ-403 batch panel: chip copy to show when the selected objects hold DIFFERENT model numbers.
	 * With it set the chip reads `Mixed` and `Edit` stays available, instead of falling back to the
	 * "Select model number" link — the selection does have model numbers, they just don't agree.
	 */
	mixedLabel?: string
	/** Forwarded to the picker: the batch selection's distinct dimension triples (REQ-403). */
	dimensionGroups?: TDimensionGroup[]
}

const toSelected = (model: TSelectableModelNumber): TSelectedModelNumber => ({
	id: model.id,
	model_number: model.model_number,
	image_from_stevens_catalogue: model.image_from_stevens_catalogue,
	dimensions: {
		width: model.dimensions.width,
		height: model.dimensions.height,
		depth: model.dimensions.depth,
		min_size: Number(model.dimensions.min_size ?? 0),
		max_size: Number(model.dimensions.max_size ?? 0),
		avg_size: Number(model.dimensions.avg_size ?? 0),
	},
})

export const ModelNumberSection = ({
	furniture,
	selectedModelNumber,
	seriesOverrideId,
	missingPriceSeriesName,
	onSelect,
	isOther = false,
	isRequired = true,
	mixedLabel,
	dimensionGroups,
	customModelNumber = null,
	isCustom = false,
}: Props) => {
	const [isOpen, setIsOpen] = useState(false)
	// A custom object's chip reads its linked definition; it never has a `selected_model_number`, so
	// the two can't both be set and the order only documents the intent.
	const modelNumber = customModelNumber?.model_number ?? selectedModelNumber?.model_number ?? mixedLabel ?? null

	const handleSave = async (
		model: TSelectableModelNumber,
		options: {
			customModelNumber: TCustomModelNumber | null
			useArbitraryEndpoint: boolean
			categoryNodeId: number | null
		},
	) => {
		await onSelect(toSelected(model), options)
	}

	return (
		<div className={section}>
			<div className={headerRow}>
				<div className={titleGroup}>
					<span className={title}>Model number</span>
					{isRequired ? <span className={requiredMark}>*</span> : null}
				</div>
				{modelNumber ? (
					<button type="button" className={editLink} onClick={() => setIsOpen(true)}>
						Edit
					</button>
				) : null}
			</div>
			{modelNumber ? (
				<div className={tagRow}>
					<span className={tag}>{modelNumber}</span>
					{missingPriceSeriesName ? (
						<Tooltip
							title={`No price for series ${missingPriceSeriesName}`}
							placement="top"
							getPopupContainer={getShadowTooltipContainer}
							styles={defaultTooltipStyles}
						>
							<span className={warningIcon}>
								<WarningOrangeIcon />
							</span>
						</Tooltip>
					) : null}
				</div>
			) : (
				<button type="button" className={selectLink} onClick={() => setIsOpen(true)}>
					Select model number
				</button>
			)}
			<SelectModelNumberModal
				isOpen={isOpen}
				onClose={() => setIsOpen(false)}
				furniture={furniture}
				seriesOverrideId={seriesOverrideId}
				initialSelectedId={selectedModelNumber?.id ?? null}
				onSave={handleSave}
				isOther={isOther}
				isCustom={isCustom}
				initialCustomModelNumberId={customModelNumber?.id ?? null}
				dimensionGroups={dimensionGroups}
			/>
		</div>
	)
}
